<template>
  <div>
    <BaseContentEditable
      ref="inputComponent"
      v-model="comment"
      rules="max:255|required"
      name="new_comment"
      :disabled="disabled"
      :placeholder="$t('comments.new_comment_placeholder')"
      :rows="2"
    >
      <div class="flex justify-end p-2 space-x-2">
        <BaseButton
          v-if="commentId || replyTo"
          look="link"
          style="white"
          @click.stop="emits('cancel')"
          >{{ $t('comments.edit_comment_cancel') }}</BaseButton
        >
        <BaseButton
          :loading="loading"
          :disabled="submitDisabled"
          @click="handleSend"
          >{{ $t('comments.new_comment_send') }}</BaseButton
        >
      </div>
    </BaseContentEditable>
  </div>
</template>
<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useNotifications } from '~/stores/notifications'
import BaseContentEditable from '~/components/base/controls/BaseContentEditable.vue'
import { useComments } from '~/stores/comments'
import { useAuth } from '~/stores/auth'
import { EntityType } from '~/models/common'
import type { UserBasicData } from '~/models/users'

const { t } = useI18n()
const { pushNotification } = useNotifications()
const commentsStore = useComments()
const authStore = useAuth()
const inputComponent = ref<InstanceType<typeof BaseContentEditable> | null>(
  null
)
const emits = defineEmits(['edit', 'create', 'sent', 'cancel', 'focus'])
const props = defineProps({
  commentId: {
    type: Number,
    default: undefined,
  },
  text: {
    type: String,
    default: '',
  },
  entityId: {
    type: Number,
    required: true,
  },
  entityType: {
    type: String as PropType<EntityType>,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  parent: {
    type: Number,
    default: 0,
  },
  replyTo: {
    type: Number,
    default: undefined,
  },
  isEditByAdmin: {
    type: Boolean,
    default: false,
  },
  initialUser: {
    type: Object as PropType<UserBasicData | null>,
    default: null,
  },
})

const comment = ref(props.text)
const loading = ref(false)

const submitDisabled = computed(
  () => !inputComponent.value?.meta.valid || !authStore.auth
)

async function handleSend() {
  if ((await inputComponent.value?.validate())?.valid) {
    try {
      loading.value = true

      if (props.commentId) {
        await commentsStore.editComment(
          props.commentId,
          comment.value,
          props.parent,
          props.isEditByAdmin,
          props.initialUser
        )

        emits('edit')
      } else {
        await commentsStore.createComment(
          props.entityId,
          comment.value,
          props.entityType,
          props.parent,
          props.replyTo
        )

        emits('create')
      }
      pushNotification({
        title: t(
          `notifications.comments.${props.commentId ? 'edit' : 'sent'}_success`
        ),
      })
      emits('sent', comment.value)
      inputComponent.value?.resetField()
    } catch (error: any) {
      if (error.status === 422 && props.commentId) {
        // edit allowed only within 15 min
        pushNotification({
          title: t(`notifications.comments.edit_timeout_error`),
          theme: 'destructive',
        })

        return
      }
      pushNotification({
        title: t(
          `notifications.comments.${props.commentId ? 'edit' : 'sent'}_error`
        ),
        theme: 'destructive',
      })
      useLogError(error)
    } finally {
      loading.value = false
    }
  }
}

function focus() {
  inputComponent.value?.focus()
}

defineExpose({ focus })
</script>
